import { Component, ChangeDetectionStrategy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';

import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PREVIEW_MODE_KEY, DEMO_ADDRESSES_KEY } from 'src/shared/services/demo-data.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

// The 3 addresses cached here must match, in id/order, the response the
// addresses.json demo template returns for GET api/v1/customer/addresses —
// RentalUnitsService.linkDemoUnitToFirstAddress() wires the by-room showcase
// units (Palmetto House, Ferncreek Rooming House) to the 1st/2nd address by
// position, so the order here is load-bearing for that cross-linking.
const PREVIEW_ADDRESSES = [
  { id: 990001, timezone: 'America/New_York', address_name: 'Palmetto House', address1: '1124 Palmetto Ave', city: 'Charleston', state: 'SC', zip: '29403' },
  { id: 990002, timezone: 'America/New_York', address_name: 'Ferncreek Rooming House', address1: '68 Ferncreek Ln', city: 'Orlando', state: 'FL', zip: '32803' },
  { id: 990003, timezone: 'America/Boise', address_name: 'Cadence Cottage', address1: '512 Cadence Way', city: 'Boise', state: 'ID', zip: '83702' },
];

/**
 * Unguarded entry point that boots an offline preview session — lets a
 * product owner click through the mock long-term-rental UI with no backend
 * or login. Seeds the same localStorage flags a real login would set, plus
 * the demo address cache DemoDataService needs to materialize templates,
 * then hard-reloads into the dashboard so every service re-initializes
 * against the seeded state (a SPA navigation would leave already-constructed
 * singletons — e.g. Auth.authenticated — holding their pre-preview values).
 */
@Component({
  selector: 'page-demo-preview',
  templateUrl: './demo-preview.page.html',
  styleUrls: ['./demo-preview.page.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DemoPreviewPage implements OnInit {
  private storage = inject(TidyStorage);

  async ngOnInit(): Promise<void> {
    // Wipe IndexedDB caches (me, addresses, dashboard reports…) AND
    // localStorage from any prior real session first — stale cached responses
    // would otherwise shadow the preview templates for their cache TTL.
    await this.storage.clear();
    this.seedPreviewSession();
    window.location.href = '/properties';
  }

  private seedPreviewSession(): void {
    localStorage.setItem('tidy_token', 'preview-token');
    localStorage.setItem('tidy_token_type', 'Bearer');
    localStorage.setItem(PREVIEW_MODE_KEY, 'true');
    localStorage.setItem('customer_id', '990000');
    localStorage.setItem('isRentalClient', 'true');
    localStorage.setItem('guest_view_type', 'rental');
    localStorage.setItem('email', 'preview@tidy.local');
    localStorage.setItem('firstName', 'Preview');
    localStorage.setItem('lastName', 'User');
    localStorage.setItem('user_role', 'owner');
    localStorage.setItem('customer_name', 'Preview User');
    // Short-circuits Me.getAccType() so it never calls Me.load() — that call
    // hits untemplated api/v1/me and would overwrite isRentalClient/customer_id
    // above with values derived from the preview interceptor's empty fallback.
    localStorage.setItem('accountType', 'rental');
    localStorage.setItem(DEMO_ADDRESSES_KEY, JSON.stringify(PREVIEW_ADDRESSES));
  }
}
